<template>
  <Card>
    <CardHeader>
      <CardTitle class="tw-flex tw-items-center tw-gap-2">
        <FileText class="tw-w-5 tw-h-5" />
        {{ t('customer_management.legals.title') }}
      </CardTitle>
      <p class="tw-text-sm tw-text-muted-foreground">
        {{ t('customer_management.legals.description') }}
      </p>
    </CardHeader>
    <CardContent>
      <div class="tw-space-y-6">
        <div>
          <Label for="legalTerms">{{ t('customer_management.legals.legal_terms') }}</Label>
          <p class="tw-text-sm tw-text-muted-foreground tw-mb-2">
            {{ t('customer_management.legals.legal_terms_help') }}
          </p>
          <Textarea
            id="legalTerms"
            :model-value="data.legals.legalTerms"
            @update:model-value="(value: string | number) => updateLegals('legalTerms', String(value))"
            :placeholder="t('customer_management.legals.legal_terms_placeholder')"
            class="tw-min-h-[120px]"
          />
        </div>

        <div>
          <Label for="termsAndConditions">
            {{ t('customer_management.legals.terms_and_conditions') }}
          </Label>
          <p class="tw-text-sm tw-text-muted-foreground tw-mb-2">
            {{ t('customer_management.legals.terms_and_conditions_help') }}
          </p>
          <Textarea
            id="termsAndConditions"
            :model-value="data.legals.termsAndConditions"
            @update:model-value="(value: string | number) => updateLegals('termsAndConditions', String(value))"
            :placeholder="t('customer_management.legals.terms_and_conditions_placeholder')"
            class="tw-min-h-[120px]"
          />
        </div>
      </div>
    </CardContent>
    <StepFooter
      v-if="!isEdit"
      :current-step="4"
      :total-steps="4"
      :loading="false"
      :on-validate="validateForm"
      @previous="handlePrevious"
      @finish="handleFinish"
      class="tw-px-6 tw-pb-6"
    />

    <div v-if="isEdit && onSave" class="tw-flex tw-px-6 tw-pb-6 tw-justify-end">
      <SectionSaveButton :on-save="onSave" :is-edit="isEdit" section-name="Legal Terms" />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import SectionSaveButton from './SectionSaveButton.vue';
import StepFooter from './StepFooter.vue';

const { t } = useI18n();

// Types
interface LegalsSectionData {
  legals: {
    legalTerms: string;
    termsAndConditions: string;
  };
}

type SaveResult = { success: boolean; message: string };

interface LegalsSectionProps {
  data: LegalsSectionData;
  isEdit: boolean;
  onSave?: () => SaveResult | Promise<SaveResult>;
}

// Props
const props = defineProps<LegalsSectionProps>();

// Emits
const emit = defineEmits<{
  change: [updates: LegalsSectionData];
  previous: [];
  finish: [];
}>();

// Methods
const updateLegals = (field: keyof typeof props.data.legals, value: string) => {
  emit('change', {
    legals: { ...props.data.legals, [field]: value },
  });
};

// Validation function for StepFooter
const validateForm = (): { isValid: boolean; errors: string[] } => {
  // Legal terms are generally optional in most cases
  return {
    isValid: true, // Always valid for now
    errors: [],
  };
};

const handlePrevious = () => {
  emit('previous');
};

const handleFinish = () => {
  emit('finish');
};
</script>
